(function () {
  'use strict';

  const CONTACT_CAPTURE_MODES = new Set(['required', 'optional', 'skip']);

  function getUiLocale(preferredLocale) {
    const locale = (preferredLocale || document.documentElement.lang || '').toString().trim();
    return locale || 'en';
  }

  function normalizeWorkerLocale(value) {
    const normalized = (value || '').toString().trim().toLowerCase();
    if (normalized.startsWith('ar')) return 'ar';
    if (normalized.startsWith('es')) return 'es';
    return 'en';
  }

  function normalizeStoreConfig(rawStore) {
    const source = rawStore && typeof rawStore === 'object' ? rawStore : {};
    let allowEmailContact = source.allowEmailContact !== false;
    let allowMobileContact = !!source.allowMobileContact;
    if (!allowEmailContact && !allowMobileContact) allowEmailContact = true;

    const contactCaptureMode = CONTACT_CAPTURE_MODES.has(String(source.contactCaptureMode || '').trim())
      ? String(source.contactCaptureMode).trim()
      : 'required';

    let whatsappUrl = typeof source.whatsappUrl === 'string' ? source.whatsappUrl.trim() : '';
    const whatsappNumberE164 = typeof source.whatsappNumberE164 === 'string' ? source.whatsappNumberE164.trim() : '';
    if (!whatsappUrl && whatsappNumberE164) {
      whatsappUrl = `https://wa.me/${whatsappNumberE164.replace(/[^\d+]/g, '').replace(/^\+/, '')}`;
    }

    return {
      key: typeof source.key === 'string' ? source.key.trim().toLowerCase() : '',
      market: typeof source.market === 'string' ? source.market.trim().toLowerCase() : '',
      brandName: typeof source.brandName === 'string' ? source.brandName.trim() : '',
      contactCaptureMode,
      allowEmailContact,
      allowMobileContact,
      whatsappNumberE164,
      whatsappUrl,
    };
  }

  function buildStorePayload(rawStore) {
    const store = normalizeStoreConfig(rawStore);
    return {
      key: store.key || null,
      market: store.market || null,
      brandName: store.brandName || null,
      contactCaptureMode: store.contactCaptureMode,
      allowEmailContact: !!store.allowEmailContact,
      allowMobileContact: !!store.allowMobileContact,
      whatsappNumberE164: store.whatsappNumberE164 || null,
      whatsappUrl: store.whatsappUrl || null,
    };
  }

  function buildWorkerContextPayload(options) {
    const locale = normalizeWorkerLocale(options && options.locale);
    const storePayload = buildStorePayload(options && options.store);
    return {
      ui_locale: locale,
      response_language: locale,
      locale,
      language: locale,
      market: storePayload.market || null,
      store: storePayload,
    };
  }

  const CONSULTATION_FAMILIES = [
    {
      id: 'onion-peptides',
      name: 'Onion+ Peptides',
      bundleHandle: 'hair-stimulation-system',
      matches: ['onion'],
    },
    {
      id: 'black-garlic-ceramides',
      name: 'Black Garlic+ Ceramides',
      bundleHandle: 'hair-strength-system',
      matches: ['black garlic'],
    },
    {
      id: 'rosemary-peptides',
      name: 'Rosemary+ Peptides',
      bundleHandle: 'hair-density-system',
      matches: ['rosemary'],
    },
    {
      id: 'strawberry-nmf',
      name: 'Strawberry+ NMF',
      bundleHandle: 'nurture-system',
      matches: ['strawberry', 'nmf'],
    },
    {
      id: 'date-multivitamins',
      name: 'Date+ Multivitamin',
      bundleHandle: 'hair-wellness-system',
      matches: ['date'],
    },
    {
      id: 'sage-multivitamin',
      name: 'Sage + Multivitamin',
      bundleHandle: 'age-well-system',
      matches: ['sage'],
    },
    {
      id: 'detox-scalp-hair-revival',
      name: 'Detox Scalp + Hair Revival',
      bundleHandle: 'scalp-hair-revival-system',
      matches: ['detox', 'charcoal', 'ghassoul', 'cactus', 'seaweed', 'revival'],
    },
  ];

  const CANONICAL_FAMILY_HANDLES = {
    'onion-peptides': {
      bundle: 'hair-stimulation-system',
      cleanse: 'volumizing-shampoo',
      condition: 'thickening-conditioner',
      treat: 'follicle-stimulating-scalp-serum',
    },
    'black-garlic-ceramides': {
      bundle: 'hair-strength-system',
      cleanse: 'invigorating-shampoo',
      mask: 'repairing-hair-mask',
      treat: 'strengthening-scalp-serum',
    },
    'rosemary-peptides': {
      bundle: 'hair-density-system',
      cleanse: 'scalp-prep-shampoo',
      condition: 'strand-thicken-conditioner',
      treat: 'follicle-boost-serum',
    },
    'strawberry-nmf': {
      bundle: 'nurture-system',
      cleanse: 'nurture-shampoo',
      condition: 'nurture-conditioner',
      protect: 'nurture-leave-in-conditioner',
    },
    'date-multivitamins': {
      bundle: 'hair-wellness-system',
      cleanse: 'nourishing-shampoo',
      condition: 'hydrating-conditioner',
      treat: 'rejuvenating-scalp-serum',
    },
    'sage-multivitamin': {
      bundle: 'age-well-system',
      cleanse: 'revitalizing-shampoo',
      condition: 'replenishing-conditioner',
      treat: 'scalp-support-serum',
    },
    'detox-scalp-hair-revival': {
      bundle: 'scalp-hair-revival-system',
      cleanse: 'charcoal-salicylic-exfoliating-shampoo',
      condition: 'ghassoul-avocado-smoothing-conditioner',
      treat: 'cactus-red-seaweed-scalp-serum',
    },
  };

  function compactText(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  function productSearchText(product) {
    const tags = Array.isArray(product && product.tags)
      ? product.tags
      : (Array.isArray(product && product.keywords) ? product.keywords : []);
    return [
      product && product.subtitle,
      product && product.tagline,
      product && product.title,
      product && product.handle,
      product && product.product_type,
      tags.join(' '),
    ].map(compactText).join(' ');
  }

  function detectConsultationFamily(product) {
    const text = productSearchText(product);
    return CONSULTATION_FAMILIES.find((family) => family.matches.some((match) => text.includes(match))) || null;
  }

  function normalizeProductType(product, family) {
    const text = productSearchText(product);
    const tags = Array.isArray(product && product.tags)
      ? product.tags
      : (Array.isArray(product && product.keywords) ? product.keywords : []);
    const hasSetTag = tags.some((tag) => compactText(tag) === 'sets');
    const handle = compactText(product && product.handle);
    const rawType = compactText(product && product.product_type);

    if (
      product && product.is_bundle === true
      || hasSetTag
      || handle.includes('-system')
      || (family && handle === compactText(family.bundleHandle))
      || rawType === 'bundle'
      || rawType === 'set'
      || rawType === 'system'
    ) {
      return { productType: 'bundle', isBundle: true, role: 'bundle' };
    }

    if (text.includes('shampoo')) return { productType: 'shampoo', isBundle: false, role: 'cleanse' };
    if (text.includes('leave-in')) return { productType: 'leave-in conditioner', isBundle: false, role: 'protect' };
    if (text.includes('conditioner')) return { productType: 'conditioner', isBundle: false, role: 'condition' };
    if (text.includes('mask')) return { productType: 'mask', isBundle: false, role: 'mask' };
    if (text.includes('serum') || text.includes('treatment')) return { productType: 'serum', isBundle: false, role: 'treat' };

    return {
      productType: product && product.product_type || '',
      isBundle: !!(product && product.is_bundle),
      role: '',
    };
  }

  function hasMeaningfulRole(product) {
    return Array.isArray(product && product.consultation_roles)
      && product.consultation_roles.some((role) => compactText(role));
  }

  function normalizeCanonicalRole(role) {
    const value = compactText(role);
    if (!value) return '';
    if (value === 'shampoo') return 'cleanse';
    if (value === 'conditioner') return 'condition';
    if (value === 'leave-in' || value === 'leave in' || value === 'finish') return 'protect';
    if (value === 'serum' || value === 'scalp_serum' || value === 'treatment') return 'treat';
    if (value === 'repair') return 'mask';
    return value;
  }

  function canonicalHandleForProduct(product, family, normalized, consultationRoles) {
    const handleMap = family && CANONICAL_FAMILY_HANDLES[family.id];
    if (!handleMap) return product && product.handle || '';
    if (normalized && normalized.isBundle) return handleMap.bundle || product.handle || '';

    const candidates = []
      .concat(consultationRoles || [])
      .concat([normalized && normalized.role, normalized && normalized.productType, product && product.product_type]);

    for (const candidate of candidates) {
      const role = normalizeCanonicalRole(candidate);
      if (role && handleMap[role]) return handleMap[role];
    }

    return product && product.handle || '';
  }

  function normalizeProductCatalog(products, bundleFamilies) {
    if (!Array.isArray(products)) return [];
    const families = bundleFamilies && typeof bundleFamilies === 'object' ? bundleFamilies : {};

    return products.map((product) => {
      if (!product || typeof product !== 'object') return product;

      const family = detectConsultationFamily(product);
      const normalized = normalizeProductType(product, family);
      const isBundle = normalized.isBundle;
      const consultationRoles = hasMeaningfulRole(product)
        ? product.consultation_roles
        : [normalized.role];
      const canonicalHandle = canonicalHandleForProduct(product, family, normalized, consultationRoles);
      const canonicalFamily = family && CANONICAL_FAMILY_HANDLES[family.id];
      const bundleHandle = isBundle ? '' : (canonicalFamily && canonicalFamily.bundle || product.bundle_handle || family && family.bundleHandle || '');

      return {
        ...product,
        handle: canonicalHandle,
        shopify_handle: product.shopify_handle || product.handle || canonicalHandle,
        canonical_handle: canonicalHandle,
        consultation_collection_id: product.consultation_collection_id || (family && family.id) || '',
        consultation_collection_name: product.consultation_collection_name || (family && family.name) || product.subtitle || product.tagline || '',
        is_bundle: isBundle,
        bundle_handle: bundleHandle,
        bundle_components: isBundle ? (families[canonicalHandle] || families[product.handle] || product.bundle_components || []) : (product.bundle_components || []),
        product_type: normalized.productType,
        consultation_roles: consultationRoles,
      };
    });
  }

  function isValidEmail(value) {
    if (!value) return false;
    const probe = document.createElement('input');
    probe.type = 'email';
    probe.value = value;
    return probe.checkValidity();
  }

  function normalizePhone(value) {
    const raw = (value || '').trim();
    if (!raw) return null;

    const normalizedDigits = raw
      .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
      .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

    let normalized = normalizedDigits
      .replace(/[^\d+]+/g, '')
      .replace(/(?!^)\+/g, '');

    if (normalized.startsWith('00')) {
      normalized = `+${normalized.slice(2)}`;
    }

    const digits = normalized.replace(/\D/g, '');
    if (digits.length < 8 || digits.length > 15) return null;

    return normalized.startsWith('+') ? `+${digits}` : digits;
  }

  function parseContactValue(rawValue, options) {
    const config = options && options.store ? normalizeStoreConfig(options.store) : normalizeStoreConfig();
    const allowEmpty = !!(options && options.allowEmpty);
    const value = (rawValue || '').trim();

    if (!value) {
      return { valid: allowEmpty, empty: true, type: 'email', value: '' };
    }

    if (value.includes('@')) {
      if (!config.allowEmailContact) {
        return { valid: false, type: 'email', value };
      }

      return {
        valid: isValidEmail(value),
        type: 'email',
        value,
      };
    }

    if (!config.allowMobileContact) {
      return {
        valid: false,
        type: 'mobile',
        value,
        normalizedValue: null,
      };
    }

    const normalizedValue = normalizePhone(value);
    return {
      valid: !!normalizedValue,
      type: 'mobile',
      value,
      normalizedValue,
    };
  }

  function normalizeContactSnapshot(rawContact) {
    if (!rawContact || typeof rawContact !== 'object') return null;

    const rawType = (rawContact.type || '').toString().trim().toLowerCase();
    const type = rawType === 'phone' ? 'mobile' : rawType;
    if (type !== 'email' && type !== 'mobile') return null;

    return {
      type,
      value: typeof rawContact.value === 'string' ? rawContact.value.trim() : '',
      normalized_value: typeof rawContact.normalized_value === 'string' ? rawContact.normalized_value.trim() : null,
    };
  }

  window.TaraConsultationStore = Object.freeze({
    getUiLocale,
    normalizeWorkerLocale,
    normalizeStoreConfig,
    buildStorePayload,
    buildWorkerContextPayload,
    isValidEmail,
    normalizePhone,
    parseContactValue,
    normalizeContactSnapshot,
    normalizeProductCatalog,
  });
})();
